import { HttpClient, PaginatedResponse, robotoHeaders } from "@/shared/http";

import {
  BoardRecord,
  CreateBoardRequest,
  UpdateBoardRequest,
  UpdateBoardAccessRequest,
} from "./BoardRecord";

/** What a caller may pass with any board request. */
export type BoardRequestOptions = Partial<{ abortSignal: AbortSignal }>;

/**
 * The boards stored on the platform: the `/v1/boards` resource.
 *
 * Writes pass `idempotent: false`, so the HTTP client sends each one once and
 * reports a network failure rather than retrying. Replayed after a lost
 * response, a create, an update and a delete would each fail: a create
 * collides with its own board on the name, an update that carries a base
 * revision fails its own revision check, and a delete comes back as not found.
 * A retry would show a person a failure they did not cause.
 */
export class PlatformBoardStore {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async create(
    request: CreateBoardRequest,
    orgId: string,
    options?: BoardRequestOptions,
  ): Promise<BoardRecord> {
    const requestUrl = this.#httpClient.constructUrl(`v1/boards/create`);

    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
      body: JSON.stringify(request),
      idempotent: false,
    });

    return response.json<BoardRecord>();
  }

  public async delete(
    boardId: string,
    orgId: string,
    options?: BoardRequestOptions,
  ): Promise<void> {
    const requestUrl = this.#httpClient.constructUrl(`v1/boards/id/${boardId}`);

    const response = await this.#httpClient.delete(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
      idempotent: false,
    });

    await response.throwIfError();
  }

  public async get(
    boardId: string,
    orgId: string,
    options?: BoardRequestOptions,
  ): Promise<BoardRecord> {
    const requestUrl = this.#httpClient.constructUrl(`v1/boards/id/${boardId}`);

    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });

    return response.json<BoardRecord>();
  }

  public async list(
    orgId: string,
    options?: BoardRequestOptions & Partial<{ pageToken: string }>,
  ): Promise<PaginatedResponse<BoardRecord>> {
    const searchParams = new URLSearchParams();
    if (options?.pageToken !== undefined) {
      searchParams.append("page_token", options.pageToken);
    }
    const requestUrl = this.#httpClient.constructUrl(`v1/boards`, searchParams);

    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });

    return response.json<PaginatedResponse<BoardRecord>>();
  }

  public async update(
    boardId: string,
    request: UpdateBoardRequest,
    orgId: string,
    options?: BoardRequestOptions,
  ): Promise<BoardRecord> {
    const requestUrl = this.#httpClient.constructUrl(`v1/boards/id/${boardId}`);

    const response = await this.#httpClient.put(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
      body: JSON.stringify(request),
      idempotent: false,
    });

    return response.json<BoardRecord>();
  }

  public async updateAccess(
    boardId: string,
    request: UpdateBoardAccessRequest,
    orgId: string,
  ): Promise<BoardRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/boards/id/${boardId}/access`,
    );

    const response = await this.#httpClient.put(requestUrl, {
      headers: robotoHeaders({ orgId }),
      body: JSON.stringify(request),
      idempotent: false,
    });

    return response.json<BoardRecord>();
  }
}
